<template>
  <v-container>

    <Guide />

    <v-dialog v-model="showGoogleMeetGuide" persistent max-width="500px">
      <GoogleMeetGuide @finishedGuide="showGoogleMeetGuide = false"/>
    </v-dialog>
    
    <div id="page-heading" class="grey--text text-h5 text-sm-h4 text-md-h3 text-l-h2 text-xl-h2 mt-2">
      Teaching Session
    </div>

    <v-row flex v-if="teachingSession">
      <v-col cols="8">
        <v-card outlined>
          <v-card-title>
            {{ teachingSession.tutorial.subject.data.title }} > {{ teachingSession.tutorial.data.title }}
          </v-card-title>
          <v-card-subtitle>
            <span class="mr-3">{{ teachingSession.data.startTime | date('EEE do MMM yyyy, HH:mm') }} - {{ teachingSession.data.endTime | date('HH:mm') }}</span>
            <v-chip v-if="teachingSession.inProgress"
              color="green"
              text-color="white"
              small
              class="text-uppercase"
            >
              In progress
            </v-chip>
            <v-chip v-if="teachingSession.inGracePeriod"
              color="red"
              text-color="white"
              small
              class="text-uppercase"
            >
              Ending soon
            </v-chip>
          </v-card-subtitle>
          <v-card-text class="mt-5">
            <div id="tutorial-html" v-html="teachingSession.tutorial.data.description"></div>
          </v-card-text>
        </v-card>

        
        <v-card outlined class="mt-5">
          <v-card-title>
            <span>
              Attendance
            </span>

            <v-spacer />
            <v-btn v-if="!offeredAll" depressed color="primary" small @click="offerAll()">
              Offer all
            </v-btn>
            <span v-else>
              <v-btn depressed color="warning" small @click="cancelAll()">
                Cancel all
              </v-btn>
              <v-btn depressed color="success" small @click="joinAll()" class="ml-1">
                Join all
              </v-btn>
            </span>
          </v-card-title>

          <v-card-text>
            
            <v-data-table
            :headers="headers"
            :items="teachingSession.attempts"
            :footer-props="{
              itemsPerPageOptions: [50, 100]
            }"
            >

              <template v-slot:item.actions="{ item }">

                <v-btn v-if="item.data.screenshareStatus == null || item.data.screenshareStatus == 'REQUESTED'" 
                  x-small 
                  depressed
                  color="primary"
                  @click="updateAttempt(item, 'Offered screenshare', 'OFFERED')">
                    Offer
                </v-btn>
                <span v-if="item.data.screenshareStatus == 'OFFERED' || item.data.screenshareStatus == 'STUDENT_JOINED'">
                  <v-btn 
                    x-small 
                    depressed 
                    color="success"
                    :loading="item.data.screenshareStatus == 'OFFERED'"
                    :disabled="item.data.screenshareStatus == 'OFFERED'"
                    @click="joinScreenshare()">
                      Join
                  </v-btn>
                  <v-btn
                    x-small
                    depressed
                    color="warning"
                    :disabled="item.data.screenshareStatus == 'STUDENT_JOINED'"
                    class="ml-1"
                    @click="updateAttempt(item, 'Cancelled screenshare', null)">
                      Cancel
                  </v-btn>
                </span>
                <span v-if="item.data.screenshareStatus == 'LECTURER_JOINED'">
                  In progress
                </span>

              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>


      <v-col cols="4" v-if="teachingSession">

        <v-card outlined>
          <v-card-title>Instructions</v-card-title>
          <v-card-text>
            <p>
              Student requests for screenshares will appear in the Screenshares card below. When you offer a screenshare you cannot join until the student has joined.
            </p>
            <p>
              Students progress is overlaid on the tutorial with icons 
              <button class="white--text v-btn v-btn--fab v-btn--round theme--light v-size--x-small primary">3</button>
              You can click on an icon to view the students, and offer a screenshare if you wish.
            </p>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-5">
          <v-card-title>
            Screenshares
          </v-card-title>
            
          <v-card-text>
            <v-simple-table fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Student</th>
                    <th class="text-right">Screenshare</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(attempt, index) in screenshareRequests" :key="index">
                    <td class="text-left">
                      {{ attempt.student.data.displayName }}
                    </td>
                    <td class="text-right screenshare-actions">
                      <v-btn v-if="attempt.data.screenshareStatus == 'REQUESTED'" 
                        x-small 
                        depressed
                        color="primary"
                        @click="updateAttempt(attempt, 'Offered screenshare', 'OFFERED')">
                          Offer
                      </v-btn>
                      <span v-if="attempt.data.screenshareStatus == 'OFFERED' || attempt.data.screenshareStatus == 'STUDENT_JOINED'">
                        <v-btn 
                          x-small 
                          depressed 
                          color="success"
                          :loading="attempt.data.screenshareStatus == 'OFFERED'"
                          :disabled="attempt.data.screenshareStatus == 'OFFERED'"
                          @click="joinScreenshare()">
                            Join
                        </v-btn>
                        <v-btn
                          x-small
                          depressed
                          color="warning"
                          :disabled="attempt.data.screenshareStatus == 'STUDENT_JOINED'"
                          class="ml-1"
                          @click="updateAttempt(attempt, 'Cancelled screenshare', null)">
                            Cancel
                        </v-btn>
                      </span>
                      <span v-if="attempt.data.screenshareStatus == 'LECTURER_JOINED'">
                        In progress
                      </span>
                    </td>
                  </tr>
                  <tr v-if="screenshareRequests.length == 0">
                    <td class="text-left">
                      - - -
                    </td>
                    <td class="text-right">
                      - - -
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <v-card v-if="selectedAttempts" outlined class="mt-5">
          <v-card-title>
            Progress
          </v-card-title>
            
          <v-card-text>
            
            <v-simple-table fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Student</th>
                    <th class="text-right">Screenshare</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(attempt, index) in selectedAttempts" :key="index">
                    <td class="text-left">
                      {{ attempt.student.data.displayName }}
                    </td>
                    <td class="text-right screenshare-actions">
                      <v-btn v-if="attempt.data.screenshareStatus == null || attempt.data.screenshareStatus == 'REQUESTED'" 
                        x-small 
                        depressed
                        color="primary"
                        @click="updateAttempt(attempt, 'Offered screenshare', 'OFFERED')">
                          Offer
                      </v-btn>
                      <span v-if="attempt.data.screenshareStatus == 'OFFERED' || attempt.data.screenshareStatus == 'STUDENT_JOINED'">
                        <v-btn 
                          x-small 
                          depressed 
                          color="success"
                          :loading="attempt.data.screenshareStatus == 'OFFERED'"
                          :disabled="attempt.data.screenshareStatus == 'OFFERED'"
                          @click="joinScreenshare()">
                            Join
                        </v-btn>
                        <v-btn
                          x-small
                          depressed
                          color="warning"
                          :disabled="attempt.data.screenshareStatus == 'STUDENT_JOINED'"
                          class="ml-1"
                          @click="updateAttempt(attempt, 'Cancelled screenshare', null)">
                            Cancel
                        </v-btn>
                      </span>
                      <span v-if="attempt.data.screenshareStatus == 'LECTURER_JOINED'">
                        In progress
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import firebase from '@/firebase.config'
import { mapGetters } from 'vuex'
import addMinutes from 'date-fns/addMinutes'
import Guide from '@/components/lecturer/Guide'
import GoogleMeetGuide from '@/components/GoogleMeetGuide'

export default {
  name: "TeachingSession",
  components: { Guide, GoogleMeetGuide },
  data: () => {
    return {
      showGoogleMeetGuide: false,
      headers: [
        { text: 'Student', value: 'student.data.displayName'},
        { text: 'Screensharing', value: 'actions', sortable: false, align: 'end'}
      ],
      selectedAttempts: null,
      screenshareWindow: null,
      desktopNotifications: [],
      offeredAll: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'user',
      lecturer: 'lecturer'
    }),
    teachingSession() {
      let session = null
      
      this.lecturer.subjects && this.lecturer.subjects.forEach(subject => {
        subject.tutorials && subject.tutorials.forEach(tutorial => {
          tutorial.teachingSessions && tutorial.teachingSessions.forEach(teachingSession => {

            if (teachingSession.id === this.$route.params.id) {
              const now = new Date()
              const endTimewithGrace = addMinutes(teachingSession.data.endTime, 15)
              if (endTimewithGrace > now) {
                // Check if the teachingSession is in progress or in the grace period
                teachingSession.inProgress = teachingSession.data.startTime < now && now < teachingSession.data.endTime
                teachingSession.inGracePeriod = teachingSession.data.endTime < now && endTimewithGrace > now

                session = teachingSession
              }
            }
          })
        })
      })
      return session
    },
    screenshareRequests() {
      let screenshareRequests = []
      if (this.teachingSession !== null && this.teachingSession.attempts) {
        screenshareRequests = this.teachingSession.attempts.filter(attempt => {
          // Only include attempts where screenshareStatus is not null
          return attempt.data.screenshareStatus !== null
        })

        // Create desktop notifications for any screenshares where the student has newly joined
        screenshareRequests.forEach(attempt => {
          if (attempt.data.screenshareStatus == 'STUDENT_JOINED' && !this.desktopNotifications.includes(attempt.id)) {
            this.desktopNotifications.push(attempt.id)
            
            this.$notification.show('LESSONSQUARED NOTIFICATION', {
              body: attempt.student.data.displayName + ' has joined the screenshare'
            }, {})
          }
          else if (attempt.data.screenshareStatus != 'STUDENT_JOINED' && this.desktopNotifications.includes(attempt.id)) {
            // Remove the flag for desktopNotification so that it can be added again for next screenshare
            this.desktopNotifications = this.desktopNotifications.filter(attemptId => {
              return attemptId != attempt.id
            })
          }
        })
      }
      return screenshareRequests
    }
  },
  watch: {
    teachingSession: {
      handler: function() {
        this.annotateStudentProgress()
      },
      deep: true
    }
  },
  methods: {
    async updateAttempt(attempt, message, screenshareStatus) {
      attempt.data.eventLog.push({
        timestamp: firebase.firestore.Timestamp.now(),
        creator: 'LECTURER',
        message: message
      })
      attempt.data.screenshareStatus = screenshareStatus
      await this.$store.dispatch('updateAttempt', attempt)

      // if this is the first time the lecturer has offered a screenshare
      if (screenshareStatus === 'OFFERED' && !this.lecturer.data.completedSteps.includes('OFFERED_SCREENSHARE')) {
        this.lecturer.data.completedSteps.push('OFFERED_SCREENSHARE')
        await this.$store.dispatch('updateLecturer', this.lecturer)
      }
        
      // Report to GA
      firebase.analytics().logEvent('screenshare', {
        action: message
      })
    },
    async joinScreenshare() {

      // Update any screenshares where the student has joined
      // Don't need to await this being completed
      this.teachingSession.attempts.forEach(attempt => {
        if (attempt.data.screenshareStatus == 'STUDENT_JOINED') {
          this.updateAttempt(attempt, 'Joined screenshare', 'LECTURER_JOINED')
        }
      })
      
      // Open the screenshare
      this.screenshareWindow = window.open(this.teachingSession.data.meetingLink, "_blank")

      // Cannot listen for the window closed event, because it is on a different domain
      // https://stackoverflow.com/questions/9388380/capture-the-close-event-of-popup-window-in-javascript/9388451
      let timer = setInterval(() => {
        if (this.screenshareWindow.closed) {
          // End any screenshares where the status is LECTURER_JOINED
          // or STUDENT_JOINED for any late arrivals
          this.teachingSession.attempts.forEach(attempt => {
            if (['LECTURER_JOINED', 'STUDENT_JOINED'].includes(attempt.data.screenshareStatus)) {
              this.updateAttempt(attempt, 'Ended screenshare', null)
            }
          })
          this.screenshareWindow = null
          clearInterval(timer)
        }
      }, 1000)
    },
    offerAll() {
      this.teachingSession.attempts.forEach(attempt => {
        // Offer screenshare for any attempt where the status is null or REQUESTED
        if([null, 'REQUESTED'].includes(attempt.data.screenshareStatus)) {
          this.updateAttempt(attempt, 'Offered screenshare', 'OFFERED')
        }
      })
      this.offeredAll = true
    },
    cancelAll() {
      this.teachingSession.attempts.forEach(attempt => {
        // Offer screenshare for any attempt where the status is null or REQUESTED
        if(['OFFERED', 'STUDENT_JOINED'].includes(attempt.data.screenshareStatus)) {
          this.updateAttempt(attempt, 'Cancelled screenshare', null)
        }
      })
      this.offeredAll = false
    },
    joinAll() {
      this.joinScreenshare()
      this.offeredAll = false
    },
    annotateStudentProgress() {
      
      if (this.teachingSession != null && this.teachingSession.attempts) {
        let studentSelectors = {}

        // Build a map from selector to [attempts]
        this.teachingSession.attempts.forEach(attempt => {
          // Check if the currentCssSelector is set 
          if (attempt.data.currentCssSelector !== null) {
            // Check if the selector is already a key
            if (!Object.prototype.hasOwnProperty.call(studentSelectors, attempt.data.currentCssSelector)) {
              studentSelectors[attempt.data.currentCssSelector] = [ attempt ] 
            }
            else {
              studentSelectors[attempt.data.currentCssSelector].push(attempt)
            }
          }
        })

        // Check if one of the groups has been selected. If so, select it after re-generating buttons
        let selectedSection
        if (this.selectedAttempts) {
          const selectedButton = document.querySelector('#tutorial-html button.v-btn--active')
          selectedSection = selectedButton.parentElement
        }  

        // Remove any buttons from the tutorial html
        document.querySelectorAll('#tutorial-html button.v-btn').forEach( element => {
          element.parentNode.removeChild(element)
        })
        // Remove any selectedAttempts from previous button click
        this.selectedAttempts = null

        // Iterate over selectors and insert a button with student count for each 
        for (const [selector, attempts] of Object.entries(studentSelectors)) {
          const numberOfStudents = attempts.length
          // handle edge case where student selected entire #tutorial-html
          let selectedElement
          if (selector === '') {
            selectedElement = document.querySelector('#tutorial-html')  
          }
          else {
            selectedElement = document.querySelector('#tutorial-html > ' + selector)
          }

          // 2020-10-14 Temporarily guard against problems with CSS selectors
          if (selectedElement != null) {            
            let btn = document.createElement('button')
            btn.className = 'white--text v-btn v-btn--fab v-btn--round theme--light v-size--x-small primary ml-2'
            btn.innerHTML = numberOfStudents
            btn.attempts = attempts
            btn.onclick = this.clickAnnotation
            selectedElement.appendChild(btn)
          }
        }

        // If one of the buttons was previously selected
        if (selectedSection) {
          // check if there's a new button - there may no longer be any students in the section
          const button = selectedSection.querySelector('button.v-btn')
          if (button) {
            this.loadAnnotatedStudents(button)
          }
        }
      }
    },
    clickAnnotation(event) {
      this.loadAnnotatedStudents(event.target)
      // Report to GA
      firebase.analytics().logEvent('view_progress')
    },
    loadAnnotatedStudents(button) {
      // Remove active class from any previously selected button
      document.querySelectorAll('#tutorial-html button.v-btn--active').forEach( element => {
        element.classList.remove('v-btn--active')
      })

      // Store selectedAttempts so that the list will be rendered
      this.selectedAttempts = button.attempts

      // Mark the button as selected
      button.classList.add('v-btn--active')
    }
  },
  mounted() {
    this.annotateStudentProgress()

    this.$vuetify.goTo('#page-heading', {
      duration: 1000,
      easing: 'easeInOutCubic'
    })

    // Ask the user to enable desktop notifications
    this.$notification.requestPermission()

    // Report to GA
    firebase.analytics().logEvent('view_tutorial')
  }
}
</script>

<style scoped>
.screenshare-actions {
  padding-right: 0px !important;
}
</style>